import { REFERENCE_MEDICATIONS, type Medication } from '@ems/clinical';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { isBackendConfigured } from '@/lib/env';
import { db, unwrap } from '@/lib/db';
import type { FormularyEntry, MedicationRow } from '@/types/db';

/** A clinical Medication plus the database metadata the UI needs. */
export interface AppMedication extends Medication {
  dbId?: string;
  slug: string;
  orgId: string | null;
  isSample: boolean;
  isCustomized: boolean;
  version?: number;
  updatedAt?: string;
  formulary?: FormularyEntry;
}

export function rowToMedication(row: MedicationRow, formulary?: FormularyEntry): AppMedication {
  return {
    id: row.slug,
    slug: row.slug,
    dbId: row.id,
    orgId: row.org_id,
    genericName: row.generic_name,
    brandNames: row.brand_names,
    drugClass: row.drug_class,
    mechanism: row.mechanism ?? undefined,
    indications: row.indications,
    contraindications: row.contraindications,
    precautions: row.precautions,
    sideEffects: row.side_effects,
    onset: row.onset ?? undefined,
    duration: row.duration ?? undefined,
    pregnancy: row.pregnancy ?? undefined,
    highAlert: row.high_alert,
    controlledSchedule: row.controlled_schedule,
    scope: row.scope,
    concentrations: row.concentrations,
    doses: row.doses,
    notes: row.notes_md ?? undefined,
    isSample: row.is_sample,
    isCustomized: row.org_id !== null,
    version: row.version,
    updatedAt: row.updated_at,
    formulary,
  };
}

const bundled: AppMedication[] = REFERENCE_MEDICATIONS.map((m) => ({
  ...m,
  slug: m.id,
  orgId: null,
  isSample: true,
  isCustomized: false,
}));

export interface MedicationsResult {
  medications: AppMedication[];
  source: 'organization' | 'library' | 'bundled';
}

/**
 * Effective formulary for the active organization. Falls back to the bundled reference formulary
 * when signed out or when no backend is configured, so dosing tools always work in the field.
 */
export function useMedications() {
  const { user, activeOrgId } = useAuth();
  const enabled = isBackendConfigured && Boolean(user);
  const query = useQuery({
    queryKey: ['medications', 'effective', activeOrgId ?? 'none'],
    enabled,
    queryFn: async (): Promise<MedicationsResult> => {
      const rows = unwrap(await db.rpc('effective_medications', { p_org: activeOrgId })) as MedicationRow[];
      let formulary: FormularyEntry[] = [];
      if (activeOrgId) {
        formulary = unwrap(await db.from('org_formulary').select('*').eq('org_id', activeOrgId)) as FormularyEntry[];
      }
      const byMed = new Map(formulary.map((f) => [f.medication_id, f]));
      return {
        medications: rows
          .map((r) => rowToMedication(r, byMed.get(r.id) ?? (r.parent_id ? byMed.get(r.parent_id) : undefined)))
          .sort((a, b) => a.genericName.localeCompare(b.genericName)),
        source: activeOrgId ? 'organization' : 'library',
      };
    },
  });
  if (!enabled) {
    return { ...query, data: { medications: bundled, source: 'bundled' } as MedicationsResult, isPending: false, isError: false };
  }
  return query;
}

export function useMedication(slug: string | undefined) {
  const result = useMedications();
  return { ...result, medication: result.data?.medications.find((m) => m.slug === slug) };
}
